import { ReactElement, memo, useCallback, useEffect, useMemo, useState } from "react"
import styled from "styled-components"

import { changeModalState, setProduct } from "reducers/products/products-actions"
import { useAppDispatch, useAppSelector } from "reducers/store"
import ProductHeader from "./components/ProductHeader"
import ProductList from "./components/ProductList"
import ProductModal from "./modals/ProductModal"
import UnpublishProductModal from "./modals/UnpublishProductModal"
import { showToast } from "reducers/toast/toast-actions"
import useProductRequest from "pages/products/hooks/useProductRequest"
import { SET_PRODUCT_LIST } from "reducers/products/actions-types"
import ApiProducts from "api/ApiProducts"
import { useQueryState } from "context/queryState"
import { useNavigate } from "react-router-dom"

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: ${(props) => props.theme.palette.gray_ice};
  margin-top: 48px;
  gap: 32px;
  overflow: hidden;
`
const InnerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: auto;
`
const Products = (): ReactElement => {
  const appDispatch = useAppDispatch()

  const navigate = useNavigate()

  useQueryState([SET_PRODUCT_LIST], async () => (await new ApiProducts().getAll()).products, SET_PRODUCT_LIST, {
    refetchOnMount: "always"
  })

  const { product, productList } = useAppSelector((state) => state.product)

  const hasData = useMemo(() => productList.data.length > 0, [productList])
  const [shouldSubmit, setShouldSubmit] = useState(false)

  const onClickEdit = useCallback((item: App.Product) => {
    navigate(`/products/update/${item.id}`)
  }, [])

  const onClickSwitch = useCallback((item: App.Product) => {
    appDispatch(setProduct(item))
    if (item.productInfo.isPublished) {
      appDispatch(changeModalState(true, "unpublish"))

      return
    }
    setShouldSubmit(true)
  }, [])

  const onSuccess = useCallback(() => {
    appDispatch(showToast("Produto republicado com sucesso!"))
  }, [])

  const { onSubmit } = useProductRequest("updatePublished", onSuccess, "Erro ao republicar produto")

  useEffect(() => {
    if (shouldSubmit && !product.productInfo.isPublished) {
      onSubmit()
      setShouldSubmit(false)
    }
  }, [product, shouldSubmit, onSubmit])

  return (
    <Container>
      <InnerContainer>
        <ProductHeader hasData={hasData} />
        <ProductList onClickEdit={onClickEdit} onClickSwitch={onClickSwitch} />
      </InnerContainer>
      <ProductModal />
      <UnpublishProductModal />
    </Container>
  )
}

export default memo(Products)
